import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Observable, tap } from 'rxjs';
import { CountryType } from '../../../types/country.type';
import { CountriesHttp } from '../../../services/countries.http';
import { BadgeType } from '../../../components/badge/types/badge.type';
import { ActivatedRoute } from '@angular/router';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';

interface SearchLocationForm {
  location: FormControl<string>;
}

@Component({
  selector: 'app-search-location-dropdown',
  templateUrl: './search-location-dropdown.component.html',
  styleUrls: ['./search-location-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchLocationDropdownComponent {
  @Input()
  isMobile: boolean = false;

  selected: CountryType[] = [];

  public innerForm = new FormGroup<SearchLocationForm>({
    location: new FormControl('', {
      nonNullable: true,
    }),
  });

  public countries$: Observable<CountryType[]> = this.countriesHttpService
    .getCountries()
    .pipe(
      tap((countries) => {
        if ('locations' in this.route.snapshot.queryParams) {
          const params: string[] | string =
            this.route.snapshot.queryParams['locations'];
          const preselectedLocations: number[] = Array.isArray(params)
            ? params.map(Number)
            : [Number(params)];

          this.selected = countries.filter((country) =>
            preselectedLocations.includes(country.country_id)
          );
          this.seFirstValueToControl();
        }
      })
    );

  @Output()
  locationSelection: EventEmitter<CountryType[]> = new EventEmitter<
    CountryType[]
  >();

  @Output()
  openChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('locationDropdown', { static: true })
  dropdown: NgbDropdown | null = null;

  constructor(
    private countriesHttpService: CountriesHttp,
    private route: ActivatedRoute
  ) {}

  public get selectedLocationBadges(): BadgeType[] {
    return this.selected.map(({ country_name, country_id }) => ({
      text: country_name,
      color: 'dark',
      closable: true,
      id: country_id,
    }));
  }

  public get locationValue(): string {
    return this.innerForm.controls.location.value
      ? this.innerForm.controls.location.value
      : 'Any location';
  }

  seFirstValueToControl(): void {
    const value = this.selected[0]?.country_name ?? '';
    this.innerForm.controls.location.setValue(value);

    if (!this.isMobile) {
      return;
    }
  }

  getUnselectedCountries(countries: CountryType[]): CountryType[] {
    return (
      countries?.filter(
        (country: CountryType) =>
          !this.selected.some(
            (location) => location.country_id === country.country_id
          )
      ) ?? []
    );
  }

  onLocationAdd(country: CountryType): void {
    this.selected.push(country);
    this.locationSelection.emit(this.selected);
    this.seFirstValueToControl();
  }

  onLocationRemove(locationId: number): void {
    this.selected = this.selected.filter(
      ({ country_id }) => country_id !== locationId
    );
    this.locationSelection.emit(this.selected);
    this.seFirstValueToControl();
  }

  clearAll(): void {
    this.selected = [];
    this.innerForm.controls.location.setValue('');
    this.locationSelection.emit(this.selected);
  }
}
